import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

interface OGSettings {
  og_title: string;
  og_description: string;
  og_image_url: string;
  site_url: string;
  twitter_title: string;
  twitter_description: string;
  twitter_image_url: string;
}

const DEFAULTS: OGSettings = {
  og_title: '모나루 (MONARU) 브랜드 무역 파트너',
  og_description: 'MONARU is the premier Korea-Vietnam bilateral brand trade agency. Expert maritime logistics, customs compliance, market entry advisory, and trade finance.',
  og_image_url: 'https://public.readdy.ai/ai/img_res/a815dbb711a8612aa9e7f5de81acb68b.jpg',
  site_url: 'https://example.com/',
  twitter_title: '모나루 (MONARU) 브랜드 무역 파트너',
  twitter_description: 'MONARU is the premier Korea-Vietnam bilateral brand trade agency.',
  twitter_image_url: 'https://readdy.ai/api/search-image?query=Korea%20Vietnam%20trade%20agency%20professional%20corporate%20banner%20navy%20silver%20minimal%20clean&width=1200&height=630&seq=og_home&orientation=landscape',
};

type SyncTab = 'og' | 'twitter';

interface Props {
  showMsg: (msg: string) => void;
}

export default function OGEditor({ showMsg }: Props) {
  const [settings, setSettings] = useState<OGSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [syncTab, setSyncTab] = useState<SyncTab>('og');
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('site_settings').select('*').eq('id', 1).maybeSingle();
    if (data) {
      setSettings({
        og_title: data.og_title || DEFAULTS.og_title,
        og_description: data.og_description || DEFAULTS.og_description,
        og_image_url: data.og_image_url || DEFAULTS.og_image_url,
        site_url: data.site_url || DEFAULTS.site_url,
        twitter_title: data.twitter_title || DEFAULTS.twitter_title,
        twitter_description: data.twitter_description || DEFAULTS.twitter_description,
        twitter_image_url: data.twitter_image_url || DEFAULTS.twitter_image_url,
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    await supabase.from('site_settings').update({
      ...settings,
      updated_at: new Date().toISOString(),
    }).eq('id', 1);
    setSaving(false);
    setShowApplyBanner(true);
    showMsg('OG 설정이 저장됐어요!');
  };

  const handleImageUpload = async (file: File) => {
    setUploading(true);
    const ext = file.name.split('.').pop();
    const fileName = `og_${Date.now()}.${ext}`;
    const { data, error } = await supabase.storage.from('og-images').upload(fileName, file, { upsert: true });
    if (error) { showMsg('이미지 업로드 실패: ' + error.message); setUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from('og-images').getPublicUrl(data.path);
    setSettings(prev => ({ ...prev, og_image_url: publicUrl, twitter_image_url: publicUrl }));
    setUploading(false);
    showMsg('이미지 업로드 완료!');
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImageUpload(file);
  };

  const syncToTwitter = () => {
    setSettings(prev => ({
      ...prev,
      twitter_title: prev.og_title,
      twitter_description: prev.og_description,
      twitter_image_url: prev.og_image_url,
    }));
    showMsg('Twitter 설정이 OG와 동기화됐어요!');
  };

  const copyText = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const previewTitle = syncTab === 'og' ? settings.og_title : settings.twitter_title;
  const previewDesc = syncTab === 'og' ? settings.og_description : settings.twitter_description;
  const previewImg = syncTab === 'og' ? settings.og_image_url : settings.twitter_image_url;
  const previewDomain = (() => {
    try { return new URL(settings.site_url).hostname; } catch { return settings.site_url; }
  })();

  if (loading) {
    return <div className="text-center py-20 text-navy/30 font-sans text-sm">불러오는 중...</div>;
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h2 className="font-serif text-navy text-xl font-bold">소셜 공유 설정 (OG Tags)</h2>
          <p className="text-navy/40 text-xs font-sans mt-1">카카오톡, 슬랙, SNS 등 공유 시 표시되는 미리보기를 설정해요.</p>
        </div>
      </div>

      {/* Apply Banner */}
      {showApplyBanner && (
        <div className="mt-4 mb-6 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-amber-500 text-base" />
          </div>
          <div className="flex-1">
            <p className="text-amber-800 text-sm font-sans font-semibold mb-1">설정이 저장됐어요!</p>
            <p className="text-amber-700 text-xs font-sans leading-relaxed">
              실제 링크 공유에 적용하려면 AI에게 <span className="font-semibold">&ldquo;저장된 OG 설정 index.html에 적용해줘&rdquo;</span> 라고 말해주세요.
            </p>
          </div>
          <button onClick={() => setShowApplyBanner(false)} className="w-5 h-5 flex items-center justify-center flex-shrink-0 cursor-pointer">
            <i className="ri-close-line text-amber-500 text-sm" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6 mt-6">
        {/* Left: Form */}
        <div className="space-y-5">
          {/* Site URL */}
          <div>
            <label className="block text-xs font-sans text-navy/50 mb-1.5 font-semibold">사이트 URL</label>
            <input
              value={settings.site_url}
              onChange={e => setSettings(prev => ({ ...prev, site_url: e.target.value }))}
              placeholder="https://monaru.com"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy"
            />
            <p className="text-[11px] text-navy/30 font-sans mt-1">공유 카드 하단에 표시되는 도메인 주소</p>
          </div>

          {/* Tabs: OG / Twitter */}
          <div className="flex items-center gap-1 bg-[#F8F9FB] rounded-lg p-1 w-fit">
            {(['og', 'twitter'] as SyncTab[]).map(t => (
              <button key={t} onClick={() => setSyncTab(t)}
                className={`px-4 py-1.5 rounded-md text-xs font-sans font-medium transition-all cursor-pointer whitespace-nowrap ${syncTab === t ? 'bg-white text-navy' : 'text-navy/40 hover:text-navy'}`}>
                {t === 'og' ? 'Open Graph' : 'Twitter Card'}
              </button>
            ))}
          </div>

          {syncTab === 'og' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-sans text-navy/50 mb-1.5">OG 제목</label>
                <input
                  value={settings.og_title}
                  onChange={e => setSettings(prev => ({ ...prev, og_title: e.target.value }))}
                  maxLength={60}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy"
                />
                <p className="text-[11px] text-navy/30 font-sans mt-1">{settings.og_title.length}/60자</p>
              </div>
              <div>
                <label className="block text-xs font-sans text-navy/50 mb-1.5">OG 설명</label>
                <textarea
                  value={settings.og_description}
                  onChange={e => setSettings(prev => ({ ...prev, og_description: e.target.value }))}
                  rows={3}
                  maxLength={160}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy resize-none"
                />
                <p className="text-[11px] text-navy/30 font-sans mt-1">{settings.og_description.length}/160자</p>
              </div>
              <div>
                <label className="block text-xs font-sans text-navy/50 mb-2">OG 이미지</label>
                {settings.og_image_url && (
                  <div className="w-full h-32 rounded-xl overflow-hidden mb-3 bg-[#F8F9FB] border border-gray-100">
                    <img src={settings.og_image_url} alt="OG preview" className="w-full h-full object-cover object-top" />
                  </div>
                )}
                <input
                  value={settings.og_image_url}
                  onChange={e => setSettings(prev => ({ ...prev, og_image_url: e.target.value }))}
                  placeholder="https://... (이미지 URL 직접 입력 또는 아래에서 업로드)"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy mb-2"
                />
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFileChange} className="hidden" />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="w-full border border-gray-200 rounded-lg py-2.5 text-sm font-sans text-navy/60 hover:border-navy hover:text-navy transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-50">
                  {uploading ? <><i className="ri-loader-4-line animate-spin" /> 업로드 중...</> : <><i className="ri-upload-cloud-line" /> 이미지 업로드 (권장: 1200×630px)</>}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-sans text-navy/40">OG와 동일하게 쓰는 경우가 많아요</p>
                <button onClick={syncToTwitter}
                  className="flex items-center gap-1.5 text-xs font-sans text-navy font-medium border border-navy/20 rounded-lg px-3 py-1.5 hover:bg-navy hover:text-white transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-link text-xs" /> OG에서 복사
                </button>
              </div>
              <div>
                <label className="block text-xs font-sans text-navy/50 mb-1.5">Twitter 제목</label>
                <input
                  value={settings.twitter_title}
                  onChange={e => setSettings(prev => ({ ...prev, twitter_title: e.target.value }))}
                  maxLength={70}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy"
                />
              </div>
              <div>
                <label className="block text-xs font-sans text-navy/50 mb-1.5">Twitter 설명</label>
                <textarea
                  value={settings.twitter_description}
                  onChange={e => setSettings(prev => ({ ...prev, twitter_description: e.target.value }))}
                  rows={3}
                  maxLength={200}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-sans text-navy/50 mb-1.5">Twitter 이미지 URL</label>
                <input
                  value={settings.twitter_image_url}
                  onChange={e => setSettings(prev => ({ ...prev, twitter_image_url: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy"
                />
              </div>
            </div>
          )}

          <button onClick={handleSave} disabled={saving}
            className="w-full bg-navy text-white text-sm font-sans font-semibold py-3 rounded-lg hover:bg-navy/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
            {saving ? '저장 중...' : '설정 저장하기'}
          </button>
        </div>

        {/* Right: Preview + HTML Snippet */}
        <div className="space-y-5">
          {/* Preview Card */}
          <div>
            <p className="text-xs font-sans text-navy/50 mb-3 font-semibold">공유 미리보기</p>
            <div className="flex gap-1 mb-3 bg-[#F8F9FB] rounded-lg p-1 w-fit">
              {(['og', 'twitter'] as SyncTab[]).map(t => (
                <button key={t} onClick={() => setSyncTab(t)}
                  className={`px-3 py-1 rounded-md text-xs font-sans cursor-pointer whitespace-nowrap transition-all ${syncTab === t ? 'bg-white text-navy' : 'text-navy/40 hover:text-navy'}`}>
                  {t === 'og' ? 'OG' : 'Twitter'}
                </button>
              ))}
            </div>
            {/* Card Mockup */}
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <div className="w-full h-40 bg-[#F8F9FB] overflow-hidden">
                {previewImg ? (
                  <img src={previewImg} alt="og preview" className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-navy/20 text-3xl" />
                  </div>
                )}
              </div>
              <div className="px-4 py-3 border-t border-gray-100">
                <p className="text-[11px] text-navy/30 font-sans uppercase tracking-wider mb-1">{previewDomain || 'example.com'}</p>
                <p className="text-sm font-sans font-semibold text-navy leading-snug line-clamp-2 mb-1">
                  {previewTitle || '제목을 입력해주세요'}
                </p>
                <p className="text-xs font-sans text-navy/50 leading-relaxed line-clamp-2">
                  {previewDesc || '설명을 입력해주세요'}
                </p>
              </div>
            </div>
            <p className="text-[11px] text-navy/30 font-sans mt-2 text-center">카카오톡 / 슬랙 / SNS 공유 시 이렇게 보여요</p>
          </div>

          {/* HTML Snippet */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-sans text-navy/50 font-semibold">현재 HTML 코드</p>
              <button
                onClick={() => copyText(
                  `<!-- Open Graph -->\n<meta property="og:title" content="${settings.og_title}" />\n<meta property="og:description" content="${settings.og_description}" />\n<meta property="og:type" content="website" />\n<meta property="og:url" content="${settings.site_url}" />\n<meta property="og:image" content="${settings.og_image_url}" />\n<!-- Twitter Card -->\n<meta name="twitter:card" content="summary_large_image" />\n<meta name="twitter:title" content="${settings.twitter_title}" />\n<meta name="twitter:description" content="${settings.twitter_description}" />\n<meta name="twitter:image" content="${settings.twitter_image_url}" />`,
                  'all'
                )}
                className="flex items-center gap-1.5 text-xs font-sans text-navy/50 hover:text-navy transition-colors cursor-pointer whitespace-nowrap">
                <i className={`${copied === 'all' ? 'ri-check-line text-green-500' : 'ri-file-copy-line'} text-xs`} />
                {copied === 'all' ? '복사됨!' : '전체 복사'}
              </button>
            </div>
            <div className="bg-[#F0F2F5] rounded-xl p-4 space-y-1 text-[11px] font-mono text-navy/60 leading-relaxed overflow-x-auto">
              <p className="text-navy/30">&lt;!-- Open Graph --&gt;</p>
              <p><span className="text-navy/50">og:title</span> = &ldquo;{settings.og_title}&rdquo;</p>
              <p><span className="text-navy/50">og:description</span> = &ldquo;{settings.og_description.slice(0, 40)}...&rdquo;</p>
              <p><span className="text-navy/50">og:url</span> = &ldquo;{settings.site_url}&rdquo;</p>
              <p><span className="text-navy/50">og:image</span> = &ldquo;{settings.og_image_url ? settings.og_image_url.slice(0, 35) + '...' : '(없음)'}&rdquo;</p>
              <p className="text-navy/30 mt-1">&lt;!-- Twitter --&gt;</p>
              <p><span className="text-navy/50">twitter:title</span> = &ldquo;{settings.twitter_title.slice(0, 30)}...&rdquo;</p>
            </div>
          </div>

          {/* Apply Instruction */}
          <div className="bg-[#F8F9FB] rounded-xl px-4 py-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-sparkling-line text-navy text-sm" />
              </div>
              <p className="text-xs font-sans text-navy font-semibold">설정 적용 방법</p>
            </div>
            <ol className="text-xs font-sans text-navy/60 space-y-1.5 leading-relaxed list-none">
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-navy text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                위에서 내용 편집 후 <strong>설정 저장하기</strong> 클릭
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-navy text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                AI에게 <span className="bg-white border border-gray-200 rounded px-1.5 py-0.5 font-semibold text-navy">&ldquo;저장된 OG 설정 적용해줘&rdquo;</span> 라고 말하기
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-navy text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                자동으로 index.html에 적용 완료!
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
